import type { ClientDto, ContactDto } from '@fos/shared/contracts/crm';
import { Archive, Copy, MoreHorizontal, Pencil, Plus, Star, TriangleAlert, UserRound } from 'lucide-react';
import { useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { Button, IconButton } from '../../../ui/Button';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '../../../ui/Menu';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON, TOUCH_TEXT } from '../../mobile';
import { ConflictActions } from './ConflictActions';
import { ContactFormDialog } from './ContactFormDialog';

/** Baner przy kontakcie: ten sam email u innego klienta (konflikt do rozstrzygnięcia). */
function ContactConflictBanner({
  client,
  contact,
  canResolve,
}: {
  client: ClientDto;
  contact: ContactDto;
  canResolve: boolean;
}) {
  const first = contact.conflicts[0];
  if (!first || !contact.email) return null;
  const names = [...new Set(contact.conflicts.map((c) => c.clientName))];
  const members = [
    {
      contactId: contact.id,
      name: contact.name,
      clientId: client.id,
      clientName: client.name,
      clientColor: client.color,
    },
    ...contact.conflicts.map((c) => ({
      contactId: c.contactId,
      name: c.contactName,
      clientId: c.clientId,
      clientName: c.clientName,
      clientColor: c.clientColor,
    })),
  ];
  return (
    <div
      role="status"
      className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-warning-subtle px-3 py-2 text-body-s text-fg"
    >
      <TriangleAlert size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-warning" />
      <p className="min-w-0 flex-1">Ten sam email ma kontakt u: {names.join(', ')}. To ta sama osoba?</p>
      {canResolve && <ConflictActions email={contact.email} members={members} variant="menu" />}
    </div>
  );
}

function ContactRow({
  client,
  contact,
  canEdit,
  onEdit,
}: {
  client: ClientDto;
  contact: ContactDto;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const utils = trpc.useUtils();
  const setPrimary = trpc.clients.contacts.setPrimary.useMutation({ onSuccess: () => utils.clients.invalidate() });
  const archive = trpc.clients.contacts.archive.useMutation({ onSuccess: () => utils.clients.invalidate() });
  const restore = trpc.clients.contacts.restore.useMutation({ onSuccess: () => utils.clients.invalidate() });
  const showConflict = contact.conflicts.length > 0 && !contact.conflictResolved && !contact.archivedAt;
  const details = [contact.role, contact.email, contact.phone].filter(Boolean).join(' · ');

  const run = async (fn: () => Promise<unknown>, done: string) => {
    try {
      await fn();
      toast.success(done);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <li className="border-b border-line py-3">
      <div className="flex items-center gap-3">
        <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-surface text-fg-muted">
          <UserRound size={16} strokeWidth={1.75} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex min-w-0 items-center gap-2 text-body-s font-medium text-fg">
            <span className="truncate">{contact.name}</span>
            {contact.isPrimary && <span className="shrink-0 text-caption font-normal text-fg-muted">główny</span>}
            {contact.archivedAt && <span className="shrink-0 text-caption font-normal text-fg-muted">archiwum</span>}
          </p>
          {details && <p className="truncate text-caption text-fg-muted">{details}</p>}
        </div>
        {contact.email && (
          <IconButton
            icon={Copy}
            label={`Kopiuj email ${contact.email}`}
            iconSize={16}
            className="hidden md:inline-flex max-md:size-11"
            onClick={() =>
              void navigator.clipboard
                ?.writeText(contact.email ?? '')
                .then(() => toast.success('Skopiowano email'))
                .catch(() => toast.error('Nie udało się skopiować'))
            }
          />
        )}
        {canEdit && (
          <Menu>
            <MenuTrigger asChild>
              <IconButton
                icon={MoreHorizontal}
                label={`Akcje: ${contact.name}`}
                iconSize={16}
                className="max-md:size-11"
              />
            </MenuTrigger>
            <MenuContent align="end">
              <MenuItem icon={Pencil} onSelect={onEdit}>
                Edytuj
              </MenuItem>
              {!contact.isPrimary && !contact.archivedAt && (
                <MenuItem
                  icon={Star}
                  onSelect={() =>
                    void run(() => setPrimary.mutateAsync({ id: contact.id }), `${contact.name} — kontakt główny`)
                  }
                >
                  Ustaw jako główny
                </MenuItem>
              )}
              <MenuSeparator />
              {contact.archivedAt ? (
                <MenuItem
                  icon={Archive}
                  onSelect={() => void run(() => restore.mutateAsync({ id: contact.id }), 'Przywrócono kontakt')}
                >
                  Przywróć
                </MenuItem>
              ) : (
                <MenuItem
                  icon={Archive}
                  onSelect={() =>
                    void run(
                      () => archive.mutateAsync({ id: contact.id, version: contact.version }),
                      'Kontakt w archiwum',
                    )
                  }
                >
                  Przenieś do archiwum
                </MenuItem>
              )}
            </MenuContent>
          </Menu>
        )}
      </div>
      {showConflict && (
        <div className="mt-2 md:pl-11">
          <ContactConflictBanner client={client} contact={contact} canResolve={canEdit} />
        </div>
      )}
    </li>
  );
}

/**
 * Sekcja „Kontakty” w Przeglądzie profilu klienta: lista osób kontaktowych (główny pierwszy), dodawanie, edycja,
 * kontakt główny, archiwizacja i baner konfliktu emaila. W trybie częściowym tylko do odczytu (DECISIONS [P]
 * „Rozstrzygnięcia pytań z Fali 1”): lista bez dodawania i edycji.
 */
export function ContactsSection({ client }: { client: ClientDto }) {
  const access = useAccess();
  const canEdit = client.canEdit && !client.partial && access.can('clients', 'edit');
  const [showArchived, setShowArchived] = useState(false);
  const contacts = trpc.clients.contacts.list.useQuery({ clientId: client.id, includeArchived: showArchived });
  const [dialog, setDialog] = useState<{ open: boolean; contact: ContactDto | null }>({ open: false, contact: null });

  const list = contacts.data ?? [];
  const activeCount = list.filter((c) => !c.archivedAt).length;

  return (
    <Section aria-labelledby="client-contacts">
      <SectionHeader
        title={<span id="client-contacts">Kontakty</span>}
        tone="strong"
        action={
          canEdit ? (
            <Button
              variant="ghost"
              size="s"
              icon={Plus}
              onClick={() => setDialog({ open: true, contact: null })}
              className={TOUCH_BUTTON}
            >
              Dodaj
            </Button>
          ) : undefined
        }
      />
      {contacts.isPending ? (
        <ListSkeleton rows={2} />
      ) : contacts.isError ? (
        <ErrorState compact onRetry={() => void contacts.refetch()} retrying={contacts.isFetching} />
      ) : list.length === 0 ? (
        <EmptyState
          compact
          icon={UserRound}
          title="Brak osób kontaktowych"
          description={canEdit ? 'Dodaj osobę, z którą rozmawiasz u klienta.' : undefined}
        />
      ) : (
        <ul className="border-t border-line">
          {list.map((c) => (
            <ContactRow
              key={c.id}
              client={client}
              contact={c}
              canEdit={canEdit}
              onEdit={() => setDialog({ open: true, contact: c })}
            />
          ))}
        </ul>
      )}
      {!contacts.isPending && !contacts.isError && (activeCount > 0 || showArchived) && (
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          className={`mt-2 rounded-xs text-caption text-fg-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
        >
          {showArchived ? 'Ukryj kontakty z archiwum' : 'Pokaż też kontakty z archiwum'}
        </button>
      )}
      {canEdit && (
        <ContactFormDialog
          open={dialog.open}
          onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
          clientId={client.id}
          contact={dialog.contact}
          firstContact={activeCount === 0}
        />
      )}
    </Section>
  );
}
